// Copyright (c) Docugami, Inc. All rights reserved.

import { TextField } from "@fluentui/react";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { validateUserEmail } from "store/AccountInfoHelpers";
import { addUserToOrg, getUserList, removeUserFromOrg } from "store/organization/actions";
import { useAsyncHelper, RequestState } from "./AsyncHelper";
import { useDgAuth } from "./DgAuthentication";
import { useAppDispatch } from "./redux";

interface IUserManagement {
    users: string[] | null;

    addUsersModal: JSX.Element;
    showAddUsersModal: () => void;
    removeUserModal: JSX.Element;
    setRemoveUserEmail: (user: string) => void;
    canRemoveUser: (user: string) => boolean;
}

export function useUserManagement(organizationId: string): IUserManagement {
    const [addUserDialogHidden, setAddUserDialogHidden] = useState(true);
    const [removeUserEmail, setRemoveUserEmail] = useState('');

    const users = useSelector((state: AppState) => state.organizations.userList);
    const { user: currentUser } = useDgAuth();

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!!organizationId) {
            dispatch(getUserList());
        }
    }, [organizationId, dispatch]);

    const canRemoveUser = useCallback((user: string) => {
        return !!currentUser?.email && user !== currentUser?.email;
    }, [currentUser]);

    return {
        users: users,

        addUsersModal: <AddUsersModal orgId={organizationId} hidden={addUserDialogHidden} onDismiss={() => setAddUserDialogHidden(true)} />,
        showAddUsersModal: () => setAddUserDialogHidden(false),
        removeUserModal: <RemoveUserModal orgId={organizationId} userEmail={removeUserEmail} hidden={!removeUserEmail} onDismiss={() => setRemoveUserEmail('')} canRemoveUser={canRemoveUser} />,
        setRemoveUserEmail: (user: string) => setRemoveUserEmail(user),
        canRemoveUser: canRemoveUser
    };
}

interface RemoveUserModalProps {
    hidden?: boolean,
    orgId: string,
    userEmail: string,
    onDismiss: () => void,
    canRemoveUser: (user: string) => boolean;
}

export const RemoveUserModal = (props: RemoveUserModalProps): JSX.Element => {
    const [removeErrorUser, setRemoveErrorUser] = useState('');

    const dispatch = useAppDispatch();
    const { execute, state } = useAsyncHelper("Remove user from org", props.orgId);

    function onRemoveUser() {
        if (!props.canRemoveUser(props.userEmail)) {
            return;
        }

        const removeUserAsync = async () => dispatch(removeUserFromOrg(props.userEmail));
        execute(removeUserAsync());
        props.onDismiss();
    }

    useEffect(() => {
        if (state === RequestState.Error) {
            setRemoveErrorUser(props.userEmail);
        }
    }, [state, props.userEmail])

    return (
        <>
            <ConfirmationDialog
                hidden={!!props.hidden}
                title={"REMOVE USER"}
                subText={`Are you sure you want to remove ${props.userEmail}?`}
                confirmButtonText={'Confirm'}
                dismissButtonText={'Cancel'}
                confirmedCallback={onRemoveUser}
                dismissedCallback={props.onDismiss} />
            <ModifyUsersErrorModal
                hidden={!removeErrorUser}
                user={removeErrorUser}
                action={'removing'}
                onDismiss={() => setRemoveErrorUser('')} />
        </>
    );
}

interface AddUsersModalProps {
    orgId: string,
    hidden?: boolean,
    onDismiss: () => void
}

const defaultInput = 'Enter User Email';
export const AddUsersModal = (props: AddUsersModalProps): JSX.Element => {
    const [userEmail, setUserEmail] = useState('');
    const [addEnabled, setAddEnabled] = useState(false);

    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const { execute, state } = useAsyncHelper("Add user to org", props.orgId);

    const dispatch = useAppDispatch();

    function onAddUser(): void {
        if (!userEmail) {
            return;
        }

        async function addUser() { await dispatch(addUserToOrg(userEmail)) }

        execute(addUser());
        props.onDismiss();
    }

    useEffect(() => {
        if (state === RequestState.Complete) {
            setUserEmail('');
            setAddEnabled(false);
        } else if (state === RequestState.Error) {
            setShowErrorMessage(true);
        }
    }, [state])

    function onGetErrorMessage(value: string) {
        return validateUserEmail(userEmail) ? '' : 'Please enter a valid email.';
    }

    const onDismiss = () => {
        setUserEmail('');
        setAddEnabled(false);
        props.onDismiss();
    }

    const onUserEmailChanged = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string | undefined) => {
        setUserEmail(newValue ?? '');
        setAddEnabled(validateUserEmail(newValue ?? ''));
    }

    return (
        <>
            <ConfirmationDialog
                hidden={!!props.hidden}
                title={"ADD USER"}
                subText={'Enter user email below to add a user'}
                confirmButtonText={'ADD'}
                confirmedCallback={onAddUser}
                dismissedCallback={onDismiss}
                disabled={!addEnabled} >
                <TextField
                    placeholder={defaultInput}
                    onChange={onUserEmailChanged}
                    onGetErrorMessage={onGetErrorMessage}
                    validateOnLoad={false}
                    deferredValidationTime={600}
                    onNotifyValidationResult={(errorMessage) => setAddEnabled(!errorMessage && !!userEmail)}
                    autoComplete="off" />
            </ConfirmationDialog>
            <ModifyUsersErrorModal
                hidden={!showErrorMessage}
                user={userEmail}
                action={'adding'}
                onDismiss={() => setShowErrorMessage(false)} />
        </>
    )
}

interface ModifyUsersErrorModalProps {
    hidden?: boolean;
    user: string;
    action: string;
    onDismiss: () => void;
}

export const ModifyUsersErrorModal = (props: ModifyUsersErrorModalProps): JSX.Element => {
    return (
        <ConfirmationDialog
            hidden={!!props.hidden}
            title={`${props.action.toLocaleUpperCase()} USER FAILED`}
            subText={`There seems to be an issue ${props.action} user ${props.user}`}
            confirmButtonText={'OK'}
            confirmedCallback={props.onDismiss}
            dismissedCallback={props.onDismiss} />
    )
}
